/**
 * **이미지 기반 유사 소재 질문** — BFF(`/api/meta-ads/similar`) 한 창구.
 *
 * ```
 *   sections/meta/components/meta-ad-similar.tsx   그리기 · 취소 (여기 없다)
 *     │ fetchSimilarAds(query, signal)
 *   이 파일                                         질문 — 쿼리 문자열과 응답 모양을 안다
 *     ▼ GET /api/meta-ads/similar?…
 *   app/api/meta-ads/similar/route.ts               답한다(관문 뒤 · `withActiveUser`)
 * ```
 *
 * ⚠️ 로그인 토큰이 **반드시 함께 나간다** — route 가 관문 뒤에 있다.
 * ⚠️ 던지는 오류 문자열이 계약이다 — 패널이 그대로 그린다(`유사 소재 조회 실패 (<상태>)`).
 * ⚠️ 응답 필드는 **거의 다 선택이다.** 상류(백엔드 유사 검색)가 보장하지 않아, 읽는 쪽이
 *    자리마다 폴백을 세운다.
 */
import { bearerAuthHeader } from 'src/lib/firebase/auth-kit';

import { resolveMockSimilarAds } from './meta-ads-mock';

/**
 * BFF 가 접어 내려 주는 유사 소재 한 장.
 *
 * 완전히 같은 이미지는 대표 한 장으로 접혀 오고(`sameImageCount`/`sameImageAds`), 묶음
 * 구성원은 대표보다 필드가 적게 온다.
 */
export type SimilarAdItem = {
  adArchiveId: string;
  pageName?: string;
  previewImage?: string;
  url?: string;
  /** `near` = pHash 복제 · `template` = 색·레이아웃 시그니처 · `clip` = CLIP 의미 유사(임베딩) */
  tier?: string;
  /** `near`·`template` 의 거리 — pHash 해밍 */
  distance?: number;
  /** `template` 의 거리 — 색 시그니처. 시그니처가 없는 소재는 오지 않는다 */
  colorDistance?: number;
  /** `clip` 의 닮은 정도 — 0~1 코사인 */
  cosine?: number;
  mediaType?: string;
  isActive?: boolean;
  adCountry?: string;
  canonicalBrands?: string[];
  /** 이미 같은 소재 묶음(이미지 클러스터)으로 잡혀 있는가 */
  sameCluster?: boolean;
  /**
   * 묶을 때 쓴 이미지 해시 — **`null` 이면 해시를 못 읽어 묶지 못한 것**이다.
   * 🚨 「같은 이미지가 없다」와 **다르다** — 화면이 그 둘을 구별해 적는다(BFF `groupBySameImage`).
   */
  phash?: string | null;
  // ---- 같은 이미지 접기 (대표에만 달린다) ----
  sameImageCount?: number;
  samePageCount?: number;
  sameImageActiveCount?: number;
  sameImageAds?: SimilarAdItem[];
};

/** 티어별 숫자 한 벌 — `counts` 는 상한 적용 «전» 전체, `shown` 은 실제로 받은 수 */
export type SimilarAdsTierCounts = {
  near?: number;
  template?: number;
  clip?: number;
};

/** BFF 응답 한 벌 — 화면이 읽는 필드만 */
export type SimilarAdsResponse = {
  configured?: boolean;
  counts?: SimilarAdsTierCounts;
  shown?: SimilarAdsTierCounts;
  note?: string;
  noHash?: boolean;
  similar?: SimilarAdItem[];
  source?: string | null;
  matched?: number;
  rawCount?: number;
  droppedSamePage?: number;
  indexed?: number;
  indexedWithColorSig?: number;
  error?: string;
};

/**
 * 조회 인자.
 * ⚠️ `adId` 와 `imageUrl` 은 **둘 중 하나만** 준다 — 원장에 없는 소재(실시간 수집분)는
 * `adId` 로 못 찾아 이미지 URL 로 즉석 해시를 계산한다.
 */
export type SimilarAdsQuery = {
  adId?: string;
  phash?: string;
  imageUrl?: string;
  maxDist?: number;
  sigMaxDist?: number;
  template?: boolean | null;
  /**
   * 의미 유사(clip) 티어를 켠다.
   * ⚠️ 수집 백엔드의 기본값은 **꺼짐**이다 — 이 칸을 켜는 쪽이 화면이다(clip 을 못 그리는
   * 옛 화면이 켜지 않으므로, 티어가 켜지는 시점이 곧 이 앱의 배포 시점이 된다).
   */
  clip?: boolean | null;
  /** 전체 상한. 티어별 상한을 안 주면 이것만 적용된다 */
  limit?: number;
  // ---- 티어별 상한 — 의미 유사(clip)가 복제(near)를 밀어내지 않게 칸을 갈라 둔다 ----
  nearLimit?: number;
  templateLimit?: number;
  clipLimit?: number;
  activeOnly?: boolean;
  beautyOnly?: boolean;
  /** clip 기준 코사인 — 백엔드 기본 0.87 · 게시물 이미지로 광고를 찾는 교차 검색은 점수가 낮게 나온다 */
  clipFloor?: number;
};

/**
 * 유사 소재 조회.
 *
 * @param query   `adId` 또는 `imageUrl` + 범위(`maxDist`·`sigMaxDist`·`template`)·`limit`
 * @param signal  앞선 조회를 끊는다 — 범위를 빠르게 바꾸면 늦은 응답이 새 결과를 덮는다
 * @throws `유사 소재 조회 실패 (<상태>)` — 응답에 `error` 가 있으면 그 문구 그대로
 */
export async function fetchSimilarAds(
  query: SimilarAdsQuery,
  signal?: AbortSignal,
): Promise<SimilarAdsResponse> {
  const mock = resolveMockSimilarAds(query);
  if (mock) return mock;

  const {
    adId, phash, imageUrl, maxDist, sigMaxDist, template, clip,
    limit, nearLimit, templateLimit, clipLimit, activeOnly, beautyOnly, clipFloor,
  } = query;

  const params = new URLSearchParams();
  if (adId) params.set('adId', String(adId));
  if (phash) params.set('phash', String(phash));
  if (imageUrl) params.set('imageUrl', String(imageUrl));
  if (Number.isFinite(maxDist)) params.set('maxDist', String(maxDist));
  if (Number.isFinite(sigMaxDist)) params.set('sigMaxDist', String(sigMaxDist));
  if (template != null) params.set('template', template ? '1' : '0');
  if (clip != null) params.set('clip', clip ? '1' : '0');
  if (Number.isFinite(limit)) params.set('limit', String(limit));
  if (Number.isFinite(nearLimit)) params.set('nearLimit', String(nearLimit));
  if (Number.isFinite(templateLimit)) params.set('templateLimit', String(templateLimit));
  if (Number.isFinite(clipLimit)) params.set('clipLimit', String(clipLimit));
  if (activeOnly) params.set('activeOnly', '1');
  if (beautyOnly) params.set('beautyOnly', '1');
  if (Number.isFinite(clipFloor)) params.set('clipFloor', String(clipFloor));

  const res = await fetch(`/api/meta-ads/similar?${params.toString()}`, {
    signal,
    // 로그인 토큰 필수
    headers: { ...(await bearerAuthHeader()) },
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `유사 소재 조회 실패 (${res.status})`);
  return data;
}
